import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { EventOrderList } from 'src/modules/event/components/order-list/component';
import { DateRange } from 'src/modules/event/interfaces/date-range';
import { IEvent } from 'src/modules/event/interfaces/event';
import { EventApiService } from 'src/modules/event/services/event-api-service';
import { EventDataSource } from 'src/modules/event/services/event-data-source';
import { OrderApiService } from 'src/modules/order/services/order-api-service';
import { OrderDataSource } from 'src/modules/order/services/order-data-source';
import moment from 'moment';
import { BehaviorSubject, Subscription as RxSubscription } from 'rxjs';
import Base = moment.unitOfTime.Base;
import { PageEvent } from '@angular/material/paginator';
import { AuthService } from 'src/app/services/auth.service';
import { OrganisationAdminApiService } from 'src/modules/role/organisation-admin/services/organisation-admin-api-service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'event-admin-dashboard',
    templateUrl: 'component.html',
    styleUrls: ['component.scss'],
    standalone: false
})
export class EventDashboard implements OnInit, OnDestroy {
  @Input() currentTab: BehaviorSubject<string>;

  @ViewChild('typeFilter', { static: false })
  public typeFilter: MatButtonToggleGroup;

  public form: FormGroup;

  public leisureUrl: string = environment.leisureUrl;
  public loading: boolean;
  public filter: string;
  public search: string;
  public isOrgAdmin: boolean;
  public isAdmin: boolean;
  public userOrgs;
  public orgSelected;

  public pageSize = 'week' as Base;

  public DateRange: DateRange;

  public Events: Event[];

  public PageEvent: PageEvent;
  public toggle: MatButtonToggleGroup;
  public displayedColumns = [
    'type',
    'event_title',
    'location_title',
    'date',
    'actions',
  ];
  public DataSource: EventDataSource;
  private Subscriptions: RxSubscription[] = [];

  constructor(
    protected EventApi: EventApiService,
    protected OrderApi: OrderApiService,
    protected Dialog: MatDialog,
    private Auth: AuthService,
    private OrganisationAdminApi: OrganisationAdminApiService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit() {
    this.isOrgAdmin = this.Auth.inGroup('organisation-admin');
    this.isAdmin = this.Auth.inGroup('admin');
    const _FormBuilder = new FormBuilder();

    const startDate = this.DateRange ? this.DateRange.startDate : new Date();

    this.setDateRange(startDate);

    if (this.isOrgAdmin) {
      const { data } = await this.OrganisationAdminApi.getOrgs(
        this.Auth.user.id,
        {},
        null,
        'organisation-admin'
      );

      this.userOrgs = data;
    }

    this.orgSelected = this.activatedRoute.snapshot.queryParamMap.get(
      'organisationId'
    )
      ? Number(this.activatedRoute.snapshot.queryParamMap.get('organisationId'))
      : 'all';

    this.DataSource = new EventDataSource(
      this.EventApi,
      'event-admin',
      'listEvents',
      {
        organisationId: this.orgSelected,
      }
    );

    this.Subscriptions.push(
      this.DataSource.loading.subscribe((loading) => (this.loading = loading))
    );
    this.Subscriptions.push(
      this.DataSource.connect().subscribe((Events) => (this.Events = Events))
    );
    this.Subscriptions.push(
      this.DataSource.PageEvent.subscribe(
        (_PageEvent) => (this.PageEvent = _PageEvent)
      )
    );
    this.Subscriptions.push(
      this.DataSource.filter.subscribe(() =>
        this.DataSource.loadWithDateRange(this.DateRange, this.PageEvent)
      )
    );
    this.Subscriptions.push(
      this.currentTab.subscribe(
        (tab) =>
          !this.loading &&
          'event-admin' === tab &&
          this.DataSource.loadWithDateRange(this.DateRange, this.PageEvent)
      )
    );

    this.form = _FormBuilder.group({
      search: [null, [Validators.required]],
    });
  }

  public guestList(Event: IEvent) {
    const config = new MatDialogConfig();
    config.disableClose = true;
    const DataSource = new OrderDataSource(this.OrderApi);

    const title = Event.event_title;
    const Item = Event;

    config.data = {
      title,
      Item,
      DataSource,
    };

    this.Subscriptions.push(
      this.Dialog.open(EventOrderList, config)
        .afterClosed()
        .subscribe((ticketId) => !!ticketId)
    );
  }

  public runManualBallot(event: IEvent) {
    confirm(
      'Are you sure you want to manually run ballot ' + event.event_title
    ) && this.EventApi.runManualBallot(event.id);
  }

  public setType(type: string) {
    this.DataSource.setFilter('type', type);
  }

  public setOrgFilter(orgId) {
    const params = { ...this.activatedRoute.snapshot.queryParams };
    delete params['organisationId'];
    this.router.navigate([], { queryParams: params });
    this.DataSource.setFilter('organisationId', orgId);
    return Number(orgId);
  }

  public setPageSize(pageSize: any) {
    const startDate = this.DateRange ? this.DateRange.startDate : new Date();

    this.pageSize = pageSize;
    this.setDateRange(startDate);
    this.DataSource.loadWithDateRange(this.DateRange);
  }

  public incrementStartDate() {
    const startDate = this.DateRange ? this.DateRange.startDate : new Date();

    this.setDateRange(moment(startDate).add(1, this.pageSize).toDate());
    this.DataSource.loadWithDateRange(this.DateRange);
  }

  public decrementStartDate() {
    const startDate = this.DateRange ? this.DateRange.startDate : new Date();

    this.setDateRange(moment(startDate).subtract(1, this.pageSize).toDate());
    this.DataSource.loadWithDateRange(this.DateRange);
  }

  public startDateToday() {
    const startDate = new Date();

    this.setDateRange(startDate);
    this.DataSource.loadWithDateRange(this.DateRange);
  }

  ngOnDestroy() {
    this.DataSource.disconnect();
    this.Subscriptions.forEach((Subscription) => Subscription.unsubscribe());
  }

  private setDateRange(startDate: Date) {
    this.DateRange = {
      startDate: moment(startDate).startOf(this.pageSize).toDate(),
      endDate:
        'day' !== this.pageSize
          ? moment(startDate).endOf(this.pageSize).toDate()
          : null,
    };
  }
}
